import React from "react";
import PropTypes from "prop-types";
import {
  Segment,
  Header,
  Icon,
  Table,
  Progress,
  Label,
  Message,
  Loader,
  Grid,
} from "semantic-ui-react";

const HEADER_COLOR = "#225745";

const SearchAnalysis = ({ frequentTerms, searchTrends = [], statsLoading }) => {
  if (statsLoading) {
    return (
      <Loader active inline="centered">
        Loading search analysis...
      </Loader>
    );
  }

  const NoDataMessage = ({ title, icon }) => (
    <Segment>
      <Header as="h3" style={{ color: HEADER_COLOR }}>
        <Icon name={icon} />
        {title}
      </Header>
      <Message info>
        No {title.toLowerCase()} available for the selected period.
      </Message>
    </Segment>
  );

  NoDataMessage.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
  };

  const FrequentSearchTerms = () => {
    if (!frequentTerms.length) {
      return <NoDataMessage title="Most Frequent Search Terms" icon="search" />;
    }

    const maxCount = frequentTerms[0]?.count || 1;

    return (
      <Segment>
        <Header as="h3" style={{ color: HEADER_COLOR }}>
          <Icon name="search" />
          Most Frequent Search Terms
        </Header>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={7}>Search Term</Table.HeaderCell>
              <Table.HeaderCell width={4}>Count</Table.HeaderCell>
              <Table.HeaderCell width={5}>Popularity</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
        </Table>
        <div
          style={{
            height: "750px",
            overflowY: "auto",
            border: "1px solid rgba(34,36,38,.15)",
            borderTop: "none",
          }}
        >
          <Table celled style={{ marginTop: 0, borderTop: "none" }}>
            <Table.Body>
              {frequentTerms.map((item, index) => (
                <Table.Row key={index}>
                  <Table.Cell width={7}>
                    <Icon name="search" />
                    {item.term}
                  </Table.Cell>
                  <Table.Cell width={4}>
                    <Label color="green">{item.count}</Label>
                  </Table.Cell>
                  <Table.Cell width={5}>
                    <Progress
                      percent={Math.round((item.count / maxCount) * 100)}
                      size="medium"
                      color="yellow"
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Segment>
    );
  };

  const DetailedSearchData = () => {
    if (!searchTrends?.length) {
      return <NoDataMessage title="Detailed Search Data" icon="table" />;
    }

    const formatDate = (dateString) =>
      new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

    const getTrend = (current, previous) => {
      if (current > previous)
        return { icon: "arrow up", color: "green", text: "Increased" };
      if (current < previous)
        return { icon: "arrow down", color: "red", text: "Decreased" };
      return { icon: "minus", color: "grey", text: "Same" };
    };

    return (
      <Segment>
        <Header as="h3" style={{ color: HEADER_COLOR }}>
          <Icon name="table" />
          Detailed Search Data
        </Header>
        <Table celled striped compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={5}>Date</Table.HeaderCell>
              <Table.HeaderCell width={5}>Searches</Table.HeaderCell>
              <Table.HeaderCell width={5}>Trend</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
        </Table>
        <div
          style={{
            height: "750px",
            overflowY: "auto",
            border: "1px solid rgba(34,36,38,.15)",
            borderTop: "none",
          }}
        >
          <Table
            celled
            striped
            compact
            style={{ marginTop: 0, borderTop: "none" }}
          >
            <Table.Body>
              {searchTrends.map((item, index) => {
                const prevSearches =
                  index > 0 ? searchTrends[index - 1].total : item.total;
                const trend = getTrend(item.total, prevSearches);

                return (
                  <Table.Row key={index}>
                    <Table.Cell>{formatDate(item.date)}</Table.Cell>
                    <Table.Cell>
                      <strong>{item.total}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Icon name={trend.icon} color={trend.color} />
                      {trend.text}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      </Segment>
    );
  };

  return (
    <Grid stackable>
      <Grid.Row>
        <Grid.Column width={8}>
          <FrequentSearchTerms />
        </Grid.Column>
        <Grid.Column width={8}>
          <DetailedSearchData />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

SearchAnalysis.displayName = "SearchAnalysis";

SearchAnalysis.propTypes = {
  frequentTerms: PropTypes.arrayOf(
    PropTypes.shape({
      term: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ).isRequired,
  searchTrends: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
    })
  ),
  statsLoading: PropTypes.bool.isRequired,
};

export default SearchAnalysis;
